import { useCallback, useEffect, useState } from "react";
import { CheckCircle, MessageSquare, Plus, RefreshCw, Star, Trash2, XCircle } from "lucide-react";
import { toast } from "sonner";
import { api, apiError } from "@/api";

const STARS = [1, 2, 3, 4, 5];

function StarPicker({ value, onChange }) {
  const [hover, setHover] = useState(0);
  return (
    <div style={{ display: "flex", gap: 4, cursor: "pointer" }}>
      {STARS.map((s) => (
        <Star
          key={s}
          size={20}
          fill={(hover || value) >= s ? "#f59e0b" : "none"}
          color={(hover || value) >= s ? "#f59e0b" : "#6b7f9e"}
          onMouseEnter={() => setHover(s)}
          onMouseLeave={() => setHover(0)}
          onClick={() => onChange(s)}
        />
      ))}
    </div>
  );
}

function AddReviewModal({ onClose, onAdded }) {
  const [form, setForm] = useState({ name: "", city: "", rating: 5, text: "" });
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.city.trim() || !form.text.trim()) {
      toast.error("Sabhi fields fill karo");
      return;
    }
    setBusy(true);
    try {
      await api.post("/api/admin/reviews", form);
      toast.success("Review add ho gaya!");
      onAdded();
      onClose();
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="retry-modal-overlay" data-testid="add-review-modal">
      <div className="retry-modal" style={{ maxWidth: 480, width: "90%" }}>
        <div className="retry-modal-head">
          <div>
            <span className="eyebrow" style={{ color: "#00d4ff" }}>ADMIN</span>
            <h3>Review Add Karo</h3>
          </div>
          <button onClick={onClose} style={{ background: "none", border: "none", cursor: "pointer", color: "#6b7f9e" }}>✕</button>
        </div>
        <form onSubmit={submit} style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          <label style={{ fontSize: 13, color: "#8899b0" }}>Customer Name
            <input
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              placeholder="e.g. Rahul Sharma"
              required
              data-testid="review-name-input"
              style={{ display: "block", width: "100%", marginTop: 4, padding: "8px 12px", borderRadius: 8, border: "1px solid #1e3050", background: "#0d1f35", color: "#e0e8f0" }}
            />
          </label>
          <label style={{ fontSize: 13, color: "#8899b0" }}>City
            <input
              value={form.city}
              onChange={(e) => setForm({ ...form, city: e.target.value })}
              placeholder="e.g. Delhi"
              required
              data-testid="review-city-input"
              style={{ display: "block", width: "100%", marginTop: 4, padding: "8px 12px", borderRadius: 8, border: "1px solid #1e3050", background: "#0d1f35", color: "#e0e8f0" }}
            />
          </label>
          <label style={{ fontSize: 13, color: "#8899b0" }}>Rating
            <div style={{ marginTop: 8 }}>
              <StarPicker value={form.rating} onChange={(r) => setForm({ ...form, rating: r })} />
            </div>
          </label>
          <label style={{ fontSize: 13, color: "#8899b0" }}>Review Text
            <textarea
              value={form.text}
              onChange={(e) => setForm({ ...form, text: e.target.value })}
              placeholder="Customer ka experience likhein (min 10 characters)"
              required
              rows={3}
              data-testid="review-text-input"
              style={{ display: "block", width: "100%", marginTop: 4, padding: "8px 12px", borderRadius: 8, border: "1px solid #1e3050", background: "#0d1f35", color: "#e0e8f0", resize: "vertical" }}
            />
          </label>
          <button className="retry-pay-btn full-width" type="submit" disabled={busy} data-testid="submit-review-button" style={{ marginTop: 4 }}>
            {busy ? <RefreshCw size={14} className="spin-slow" /> : <Plus size={14} />}
            {busy ? "Saving…" : "Review Publish Karo"}
          </button>
        </form>
      </div>
    </div>
  );
}

const STATUS_MAP = {
  pending: { label: "Pending", color: "#f59e0b" },
  approved: { label: "Approved", color: "#22c55e" },
  rejected: { label: "Rejected", color: "#ef4444" },
};

export default function AdminReviewManager({ query = "" }) {
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [showAdd, setShowAdd] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = filter !== "all" ? `?status=${filter}` : "";
      const { data } = await api.get(`/api/admin/reviews${params}`);
      setReviews(data);
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setLoading(false);
    }
  }, [filter]);

  useEffect(() => { load(); }, [load]);

  const updateStatus = async (id, status) => {
    try {
      await api.patch(`/api/admin/reviews/${id}`, { status });
      toast.success(status === "approved" ? "Review approved!" : "Review rejected");
      load();
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  const deleteReview = async (id) => {
    if (!window.confirm("Yeh review delete karein?")) return;
    try {
      await api.delete(`/api/admin/reviews/${id}`);
      toast.success("Review deleted");
      load();
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  const visible = reviews.filter((r) => {
    if (!query) return true;
    const q = query.toLowerCase();
    return r.name?.toLowerCase().includes(q) || r.city?.toLowerCase().includes(q) || r.text?.toLowerCase().includes(q);
  });

  return (
    <section className="admin-manager" data-testid="admin-review-manager">
      {showAdd && <AddReviewModal onClose={() => setShowAdd(false)} onAdded={load} />}
      <div className="admin-panel">
        <div className="panel-head">
          <div>
            <h2>Customer Reviews</h2>
            <span>{reviews.length} total · {reviews.filter((r) => r.status === "approved").length} live</span>
          </div>
          <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
            <button
              onClick={() => setShowAdd(true)}
              className="primary-btn"
              style={{ padding: "6px 14px", fontSize: 13 }}
              data-testid="add-review-button"
            >
              <Plus size={14} style={{ marginRight: 4 }} /> Add Review
            </button>
            <RefreshCw size={17} style={{ cursor: "pointer" }} onClick={load} />
          </div>
        </div>

        {/* Filter tabs */}
        <div className="order-tabs" style={{ marginBottom: 16 }}>
          {[["all", "All"], ["pending", "Pending"], ["approved", "Approved"], ["rejected", "Rejected"]].map(([v, t]) => (
            <button
              key={v}
              className={filter === v ? "active" : ""}
              onClick={() => setFilter(v)}
              data-testid={`review-filter-${v}`}
            >
              {t}
              {v === "pending" && reviews.filter((r) => r.status === "pending").length > 0 && (
                <span className="tab-badge">{reviews.filter((r) => r.status === "pending").length}</span>
              )}
            </button>
          ))}
        </div>

        {loading ? (
          <p className="admin-empty-copy">Loading reviews…</p>
        ) : visible.length === 0 ? (
          <div className="empty-state" data-testid="no-reviews-state">
            <MessageSquare size={30} />
            <h3>Koi reviews nahi hain</h3>
            <p>Pehla review add karo ya filter change karo</p>
          </div>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            {visible.map((r) => (
              <div
                key={r.id}
                className="admin-panel"
                style={{ padding: "14px 16px", borderRadius: 10, border: "1px solid #1e3050" }}
                data-testid={`review-card-${r.id}`}
              >
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 12 }}>
                  <div style={{ flex: 1 }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4 }}>
                      <div style={{ width: 32, height: 32, borderRadius: "50%", background: "#0d1f35", display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 700, color: "#00d4ff", fontSize: 14 }}>
                        {r.name?.[0]?.toUpperCase()}
                      </div>
                      <div>
                        <b style={{ fontSize: 14 }}>{r.name}</b>
                        <small style={{ display: "block", color: "#6b7f9e", fontSize: 11 }}>{r.city} · {r.is_verified ? "Verified Purchase" : "Manual"}</small>
                      </div>
                      <div style={{ marginLeft: "auto", display: "flex", gap: 2 }}>
                        {[1, 2, 3, 4, 5].map((s) => (
                          <Star key={s} size={12} fill={r.rating >= s ? "#f59e0b" : "none"} color={r.rating >= s ? "#f59e0b" : "#3a4f6a"} />
                        ))}
                      </div>
                    </div>
                    <p style={{ fontSize: 13, color: "#c0cfe0", margin: "6px 0 4px", lineHeight: 1.5 }}>"{r.text}"</p>
                    <small style={{ color: "#4a6080", fontSize: 11 }}>{new Date(r.created_at).toLocaleDateString("en-IN")}</small>
                  </div>
                  <div style={{ display: "flex", flexDirection: "column", gap: 6, alignItems: "flex-end" }}>
                    <span
                      style={{ padding: "2px 8px", borderRadius: 20, fontSize: 11, fontWeight: 600, background: `${STATUS_MAP[r.status]?.color}22`, color: STATUS_MAP[r.status]?.color }}
                    >
                      {STATUS_MAP[r.status]?.label}
                    </span>
                    <div style={{ display: "flex", gap: 4 }}>
                      {r.status !== "approved" && (
                        <button
                          onClick={() => updateStatus(r.id, "approved")}
                          style={{ padding: "4px 8px", borderRadius: 6, background: "#22c55e22", border: "1px solid #22c55e44", color: "#22c55e", cursor: "pointer", fontSize: 11 }}
                          data-testid={`approve-review-${r.id}`}
                        >
                          <CheckCircle size={12} /> Approve
                        </button>
                      )}
                      {r.status !== "rejected" && (
                        <button
                          onClick={() => updateStatus(r.id, "rejected")}
                          style={{ padding: "4px 8px", borderRadius: 6, background: "#ef444422", border: "1px solid #ef444444", color: "#ef4444", cursor: "pointer", fontSize: 11 }}
                          data-testid={`reject-review-${r.id}`}
                        >
                          <XCircle size={12} /> Reject
                        </button>
                      )}
                      <button
                        onClick={() => deleteReview(r.id)}
                        style={{ padding: "4px 8px", borderRadius: 6, background: "#ef444411", border: "1px solid #ef444433", color: "#ef4444", cursor: "pointer", fontSize: 11 }}
                        data-testid={`delete-review-${r.id}`}
                      >
                        <Trash2 size={12} />
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </section>
  );
}
